import CustomLoading from '@/components/custom-loading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { GiftCardTransactionsFragment } from '@/gql/generated';
import { ErrorType } from '@/lib/handle-error';
import { formatDateToTimezone } from '@/lib/timezone';
import { formatNumberToCurrency } from '@/lib/utils';
import { transactionsType } from '@/utils/constant';
import { getGiftCardCode } from '@/utils/gift-card';
import { UseInfiniteQueryResult } from '@tanstack/react-query';
import clsx from 'clsx';
import { useCallback, useContext, useEffect, useRef } from 'react';
import NoTransaction from '../../_components/no-transaction';
import { AuthenticatedContext } from '../../../_provider/authenticated-provider';

const TransactionTable = (props: {
  startDate: Date;
  endDate: Date;
  whmcsId: number | undefined;
  queryResult:
    | UseInfiniteQueryResult<GiftCardTransactionsFragment[], ErrorType>
    | undefined;
  handleClick?: (item: GiftCardTransactionsFragment) => void;
}) => {
  const { user } = useContext(AuthenticatedContext);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;

        // once the user has scrolled within 500px of the bottom of the table, fetch more data if we can
        if (
          scrollHeight - scrollTop - clientHeight < 500 &&
          props.queryResult &&
          !props.queryResult.isFetching &&
          props.queryResult.hasNextPage
        ) {
          props.queryResult.fetchNextPage();
        }
      }
    },
    [
      props.queryResult?.fetchNextPage,
      props.queryResult?.isFetching,
      props.queryResult?.hasNextPage,
    ],
  );
  if (!props.queryResult) {
    return <NoTransaction />;
  }
  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);
  return (
    <div
      className="overflow-auto h-full pb-11 relative border-t border-t-gray-border"
      onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
      ref={tableContainerRef}
    >
      <Table>
        <TableHeader className="sticky -top-1 z-10 h-16 w-full bg-gray-lightest ring-1 ring-offset-0 ring-gray-border">
          <TableRow className="font-semibold text-2xl text-gray-primary">
            <TableHead className="pl-6 text-gray-primary">Gift code</TableHead>
            <TableHead className="text-gray-primary">Transaction ID</TableHead>
            <TableHead className="text-gray-primary">Date & Time</TableHead>
            <TableHead className="text-gray-primary">Amount</TableHead>
            <TableHead className="text-gray-primary">Type</TableHead>
            <TableHead className="text-gray-primary">Balance</TableHead>
          </TableRow>
        </TableHeader>
        {props.queryResult.data?.length === 0 && (
          <div className="w-full absolute h-full top-0 flex justify-center items-center">
            <NoTransaction />
          </div>
        )}
        {props.queryResult.isLoading && (
          <div className="flex justify-center items-center w-full h-full absolute top-1/4">
            <CustomLoading />
          </div>
        )}
        <TableBody className="text-2xl text-gray-primary cursor-pointer">
          {props.queryResult.data?.map((item) => {
            const isAddFund = item.type === transactionsType.TOP_UP;
            return (
              <TableRow
                key={item.id}
                onClick={() => props.handleClick?.(item)}
                className="border-gray-border"
              >
                <TableCell className="font-medium pl-6">
                  {getGiftCardCode(
                    item.gift_card?.code ?? '',
                    item.gift_card?.pos_gift_card?.code,
                  )}
                </TableCell>
                <TableCell>{item.code}</TableCell>
                <TableCell>
                  <div>
                    {formatDateToTimezone(
                      item.date,
                      user?.time_zone!,
                      'hh:mm:ss a',
                    )}
                  </div>
                  <div className="text-xl font-normal text-gray-secondary">
                    {formatDateToTimezone(
                      item.date,
                      user?.time_zone!,
                      'MM/dd/yyyy',
                    )}
                  </div>
                </TableCell>
                <TableCell
                  className={clsx(
                    'text-2xl font-bold',
                    isAddFund ? 'text-success' : 'text-danger-discount',
                  )}
                >
                  {isAddFund ? '+' : '-'}
                  {formatNumberToCurrency(item.balance_change)}
                </TableCell>
                <TableCell> {isAddFund ? 'Add funds' : 'Redeem'}</TableCell>
                <TableCell className="font-bold">
                  {formatNumberToCurrency(item.balance_after)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {props.queryResult.isFetchingNextPage && (
        <div className="flex items-center justify-center">
          <CustomLoading />
        </div>
      )}
    </div>
  );
};

export default TransactionTable;
